import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import * as React from 'react';

import { useElementHeight } from '@/hooks/useElementHeight';
import { useWindowHeight } from '@/hooks/useWindowHeight';
import {
  Blueprint,
  BlueprintPortal,
  useBlueprint,
} from '@/layouts/components/Blueprint';

const scrollbar = {
  '::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
};

type CartLayoutContextType = {
  footerHeight: number;
};

const CartLayoutContext = React.createContext<CartLayoutContextType>({
  footerHeight: 0,
});

export const useCartLayout = () => React.useContext(CartLayoutContext);

export const CartLayoutMain: React.FC = ({ children }) => {
  return (
    <Box
      as="main"
      flexGrow={1}
      minW={0}
      maxW="container.xl"
      w="100%"
      mx="auto"
      px={{ base: 4, md: 4 }}
      py={{ base: 4, md: 6 }}
      overflowY="auto"
      sx={scrollbar}
    >
      {children}
    </Box>
  );
};

export const CartLayoutAside: React.FC<{ side: 'left' | 'right' }> = ({
  children,
  side,
}) => {
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const { headerHeight } = useBlueprint();
  const windowHeight = useWindowHeight();
  const contentHeight = windowHeight
    ? `calc(${windowHeight}px - ${headerHeight}px)`
    : '100vh';

  if (isMobile) return null;

  return (
    <Box
      as="aside"
      width={{ base: '100%', lg: '280px' }}
      flexShrink={0}
      position="sticky"
      top={`${headerHeight}px`}
      height={contentHeight}
      overflowY="auto"
      bg="white"
      borderLeftWidth={side === 'right' ? '1px' : 0}
      borderRightWidth={side === 'left' ? '1px' : 0}
      borderColor="gray.200"
      p={4}
      sx={scrollbar}
    >
      {children}
    </Box>
  );
};

export const CartLayoutPortal = BlueprintPortal;

interface CartLayoutProps {
  leftSidebar?: React.ReactNode;
  rightSidebar?: React.ReactNode;
  children: React.ReactNode;
}

export const CartLayout: React.FC<CartLayoutProps> = ({
  leftSidebar,
  rightSidebar,
  children,
}) => {
  const footer = useElementHeight();
  const { headerHeight } = useBlueprint();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  return (
    <CartLayoutContext.Provider value={{ footerHeight: footer.height }}>
      <Blueprint>
        <Flex minH={`calc(100vh - ${headerHeight}px)`}>
          {!isMobile && leftSidebar && (
            <CartLayoutAside side="left">{leftSidebar}</CartLayoutAside>
          )}

          <CartLayoutMain>{children}</CartLayoutMain>

          {!isMobile && rightSidebar && (
            <CartLayoutAside side="right">{rightSidebar}</CartLayoutAside>
          )}
        </Flex>
      </Blueprint>
    </CartLayoutContext.Provider>
  );
};
